import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { Input, Select, Skeleton } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faHeart, faHotel, faLocationDot, faMagnifyingGlass, faStar } from '@fortawesome/free-solid-svg-icons';

import { ISavedProperties } from '@share/common-types';
import {
  deleteSavedProperties,
  getSavedProperties,
  ISavedPropertiesState,
  removeSavedProperties,
} from '@share/store/slices';
import { Routes } from '@share/constants';
import { FunctionalRootState, RootState } from '@share/utils';

import { ModalConfirmation } from '../../../common/modal-confirmation';
import { AccountEmptyState } from '../empty-state';

import styles from './styles.module.scss';

const PAGE_SIZE = 9;
type SortKey = 'created' | 'reviewsCount' | 'rating';

interface IProps {
  accountUsername: string;
}

// Same slug rules as the legacy saved-properties page, so detail links don't change.
const getDetailPath = (accountUsername: string, property: ISavedProperties): string => {
  const slug = (property.name || '')
    .replace(/\s+/g, '-')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  const base = property.type === 'hotel' ? Routes.Hotel : Routes.Condo;
  return `/${accountUsername}${base}/${property.propertyId}/${slug || `property-${property.propertyId}`}`;
};

/** Saved properties as photo cards, reusing the saved-properties slice (fetch, sort, remove one, remove all). */
export const AccountSavedProperties = ({ accountUsername }: IProps): React.ReactElement => {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();
  const { properties, loading }: ISavedPropertiesState = useSelector((state: FunctionalRootState) => state.savedPropertiesStore);

  const [sort, setSort] = useState<SortKey>('created');
  const [query, setQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [toRemove, setToRemove] = useState<ISavedProperties | 'all' | null>(null);
  // Image URLs that failed to load fall back to the placeholder instead of a broken image.
  const [brokenImages, setBrokenImages] = useState<Record<number, boolean>>({});

  useEffect(() => {
    dispatch(getSavedProperties(sort));
  }, [sort]);

  const filtered = useMemo(() => {
    const text = query.trim().toLowerCase();
    return text ? properties.filter(property => (property.name || '').toLowerCase().includes(text)) : properties;
  }, [properties, query]);

  const shown = filtered.slice(0, visibleCount);
  const hasProperties = properties.length > 0;

  const handleConfirm = () => {
    if (toRemove === 'all') {
      dispatch(removeSavedProperties());
    } else if (toRemove) {
      dispatch(deleteSavedProperties(toRemove.propertyId));
    }
    setToRemove(null);
  };

  if (loading && !hasProperties) {
    return (
      <div className={styles.grid}>
        {[0, 1, 2].map(key => (
          <div key={key} className={styles.card}>
            <div className={styles.media} />
            <div className={styles.body}><Skeleton active paragraph={{ rows: 2 }} /></div>
          </div>
        ))}
      </div>
    );
  }

  if (!hasProperties) {
    return (
      <AccountEmptyState
        variant="panel"
        icon={faHeart}
        title={intl.formatMessage({ id: 'my.account.hub.saved.empty_title' })}
        body={intl.formatMessage({ id: 'my.account.hub.saved.empty_body' })}
        action={{ label: intl.formatMessage({ id: 'my.account.hub.saved.search' }), to: `/${accountUsername}` }}
      />
    );
  }

  return (
    <div className={styles.savedProperties}>
      <div className={styles.toolbar}>
        <span className={styles.count}>
          {intl.formatMessage({ id: 'my.account.hub.saved.count' }, { count: properties.length })}
        </span>
        <div className={styles.controls}>
          <div className={styles.searchWrap}>
            <Input
              allowClear
              value={query}
              onChange={event => {
                setQuery(event.target.value);
                setVisibleCount(PAGE_SIZE);
              }}
              prefix={<FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />}
              placeholder={intl.formatMessage({ id: 'map.search.placeholder' })}
              aria-label={intl.formatMessage({ id: 'map.search.placeholder' })}
            />
          </div>
          <Select<SortKey>
            value={sort}
            onChange={setSort}
            className={styles.sort}
            dropdownMatchSelectWidth={false}
            aria-label={intl.formatMessage({ id: 'my.account.hub.saved.sort' })}
            options={[
              { value: 'created', label: intl.formatMessage({ id: 'my.account.hub.saved.sort_recent' }) },
              { value: 'reviewsCount', label: intl.formatMessage({ id: 'my.account.hub.saved.sort_popular' }) },
              { value: 'rating', label: intl.formatMessage({ id: 'my.account.hub.saved.sort_rating' }) },
            ]}
          />
          <button type="button" className={styles.removeAll} onClick={() => setToRemove('all')}>
            {intl.formatMessage({ id: 'my.account.hub.saved.remove_all' })}
          </button>
        </div>
      </div>

      {filtered.length ? (
        <ul className={styles.grid}>
          {shown.map(property => {
            const isHotel = property.type === 'hotel';
            return (
              <li key={property.propertyId} className={styles.card}>
                <div className={styles.media}>
                  {property.imgSrc && !brokenImages[property.propertyId] ? (
                    <img
                      src={property.imgSrc}
                      alt=""
                      loading="lazy"
                      className={styles.image}
                      onError={() => setBrokenImages(current => ({ ...current, [property.propertyId]: true }))}
                    />
                  ) : (
                    <div className={styles.placeholder} aria-hidden="true">
                      <FontAwesomeIcon icon={isHotel ? faHotel : faBuilding} />
                    </div>
                  )}
                  <button
                    type="button"
                    className={styles.heart}
                    onClick={() => setToRemove(property)}
                    aria-label={intl.formatMessage({ id: 'my.account.hub.saved.remove' }, { name: property.name })}
                  >
                    <FontAwesomeIcon icon={faHeart} aria-hidden="true" />
                  </button>
                  {property.type ? <span className={styles.type}>{property.type}</span> : null}
                </div>
                <div className={styles.body}>
                  <h3 className={styles.name} title={property.name}>{property.name}</h3>
                  {property.address ? (
                    <p className={styles.address} title={property.address}>
                      <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
                      <span>{property.address}</span>
                    </p>
                  ) : null}
                  <div className={styles.footer}>
                    {/* Ratings are out of 5; anything outside that range is bad data, so it isn't shown. */}
                    {property.rating > 0 && property.rating <= 5 ? (
                      <span className={styles.rating}>
                        <FontAwesomeIcon icon={faStar} className={styles.star} aria-hidden="true" />
                        <b>{property.rating.toFixed(1)}</b>
                        <span className={styles.reviews}>
                          {intl.formatMessage({ id: 'my.account.hub.saved.reviews' }, { count: property.reviewsCount || 0 })}
                        </span>
                      </span>
                    ) : <span />}
                    <a
                      href={getDetailPath(accountUsername, property)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.checkRates}
                    >
                      {intl.formatMessage({ id: 'my.account.hub.saved.check_rates' })}
                    </a>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <AccountEmptyState
          variant="panel"
          icon={faMagnifyingGlass}
          title={intl.formatMessage({ id: 'my.account.hub.saved.no_match' }, { query: query.trim() })}
          action={{ label: intl.formatMessage({ id: 'my.account.hub.saved.clear_search' }), onClick: () => setQuery('') }}
        />
      )}

      {filtered.length > visibleCount ? (
        <button type="button" className={styles.showMore} onClick={() => setVisibleCount(count => count + PAGE_SIZE)}>
          {intl.formatMessage({ id: 'my.account.hub.saved.show_more' }, { count: filtered.length - visibleCount })}
        </button>
      ) : null}

      <ModalConfirmation
        message={toRemove === 'all' ? 'delete.properties.message' : 'delete.property.message'}
        title={toRemove === 'all' ? 'delete.properties' : 'delete.property'}
        yesMessage="confirm"
        noMessage="cancel"
        visible={!!toRemove}
        onCancel={() => setToRemove(null)}
        onConfirm={handleConfirm}
      />
    </div>
  );
};
